import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Edit2, X, Plus, Trash2, Camera, History } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const getDefaultEntries = () => {
  const entries = [];
  const now = new Date();
  for (let i = 6; i >= 1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    entries.push({ month: d.getMonth() + 1, year: d.getFullYear(), value: "", note: "" });
  }
  return entries;
};

const formatCurrency = (v) => {
  const num = parseFloat(v);
  if (isNaN(num)) return "-";
  return `R ${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

export const HistoricalStockValues = () => {
  const [entries, setEntries] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [histRes, snapRes] = await Promise.all([
        axios.get(`${API}/reports/stock/historical-values`),
        axios.get(`${API}/reports/stock/snapshots`),
      ]);
      setEntries(histRes.data.entries?.length > 0 ? histRes.data.entries : getDefaultEntries());
      setSnapshots(snapRes.data.snapshots || []);
      setLoaded(true);
    } catch {
      setEntries(getDefaultEntries());
      setSnapshots([]);
      setLoaded(true);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const valid = entries.filter(e => e.value !== "" && e.value !== null && e.value !== undefined);
      await axios.put(`${API}/reports/stock/historical-values`, { entries: valid });
      toast.success("Historical stock values saved");
      setEditing(false);
      fetchAll();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const captureSnapshot = async () => {
    setCapturing(true);
    try {
      await axios.post(`${API}/reports/stock/snapshots/capture`, { note: "Manual snapshot" });
      toast.success("Stock valuation snapshot captured");
      fetchAll();
    } catch {
      toast.error("Failed to capture snapshot");
    } finally {
      setCapturing(false);
    }
  };

  const updateEntry = (index, field, value) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
  };

  const addRow = () => {
    const last = entries[entries.length - 1];
    let newMonth = 1, newYear = new Date().getFullYear();
    if (last) {
      newMonth = last.month === 12 ? 1 : last.month + 1;
      newYear = last.month === 12 ? last.year + 1 : last.year;
    }
    setEntries([...entries, { month: newMonth, year: newYear, value: "", note: "" }]);
  };

  const removeRow = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  if (!loaded) return null;

  // Merge manual entries and snapshots into a single timeline
  const allRecords = [];

  // Add manual entries (from previous system)
  for (const e of entries) {
    if (e.value !== "" && e.value !== null && e.value !== undefined) {
      allRecords.push({
        month: e.month, year: e.year,
        cost_value: parseFloat(e.value) || 0,
        note: e.note || "",
        source: "manual",
        key: `manual-${e.year}-${e.month}`,
      });
    }
  }

  // Add system snapshots
  for (const s of snapshots) {
    allRecords.push({
      month: s.month, year: s.year,
      cost_value: s.cost_value || 0,
      retail_value: s.retail_value || 0,
      product_count: s.product_count || 0,
      total_qty: s.total_qty || 0,
      note: s.note || "",
      date: s.date,
      source: "system",
      key: `system-${s.year}-${s.month}`,
    });
  }

  // Sort by date descending
  allRecords.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    if (a.month !== b.month) return b.month - a.month;
    return a.source === "system" ? -1 : 1;
  });

  return (
    <div className="space-y-4">
      {/* System Snapshots */}
      <Card data-testid="stock-snapshots">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="h-4 w-4" />
              Stock Valuation History
            </CardTitle>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={captureSnapshot}
              disabled={capturing}
              data-testid="capture-snapshot-btn"
            >
              <Camera className="h-3 w-3 mr-1" />
              {capturing ? "Capturing..." : "Snapshot Now"}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            System snapshots are taken automatically on the 1st of each month. Manual entries are from your previous system.
          </p>
        </CardHeader>
        <CardContent>
          {allRecords.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              No history yet. Click "Snapshot Now" to capture current stock value, or add manual entries below.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Month</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Cost Value</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Retail Value</th>
                    <th className="text-center py-2 px-3 font-medium text-gray-600">Source</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {allRecords.map((r) => (
                    <tr key={r.key} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium">
                        {MONTH_NAMES[(r.month || 1) - 1]} {r.year}
                      </td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-gray-800">
                        {formatCurrency(r.cost_value)}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-gray-600">
                        {r.retail_value ? formatCurrency(r.retail_value) : "-"}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          r.source === "system"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          {r.source === "system" ? "SYSTEM" : "MANUAL"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-500 text-xs">{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Entry (Previous System) */}
      <Card data-testid="historical-stock-values">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Manual Entry (Previous System)</CardTitle>
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(false); fetchAll(); }}>
                    <X className="h-3 w-3 mr-1" /> Cancel
                  </Button>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving}>
                    <Save className="h-3 w-3 mr-1" /> {saving ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-historical-btn">
                  <Edit2 className="h-3 w-3 mr-1" /> Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-3 font-medium text-gray-600 w-32">Month</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600 w-44">Stock Value</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Note</th>
                  {editing && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">
                      {editing ? (
                        <div className="flex gap-1">
                          <select
                            value={entry.month}
                            onChange={(e) => updateEntry(i, "month", parseInt(e.target.value))}
                            className="border rounded px-1 py-1 text-xs w-16"
                          >
                            {MONTH_NAMES.map((m, mi) => (
                              <option key={mi} value={mi + 1}>{m}</option>
                            ))}
                          </select>
                          <Input
                            type="number"
                            value={entry.year}
                            onChange={(e) => updateEntry(i, "year", parseInt(e.target.value) || 2025)}
                            className="h-7 w-20 text-xs"
                          />
                        </div>
                      ) : (
                        <span className="font-medium">
                          {MONTH_NAMES[(entry.month || 1) - 1]} {entry.year}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {editing ? (
                        <Input
                          type="number"
                          step="0.01"
                          value={entry.value}
                          onChange={(e) => updateEntry(i, "value", e.target.value)}
                          placeholder="0.00"
                          className="h-7 text-right text-xs"
                          data-testid={`stock-value-input-${i}`}
                        />
                      ) : (
                        <span className="font-mono font-semibold text-gray-800">
                          {entry.value !== "" && entry.value !== null ? formatCurrency(entry.value) : "-"}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {editing ? (
                        <Input
                          value={entry.note || ""}
                          onChange={(e) => updateEntry(i, "note", e.target.value)}
                          placeholder="Optional note..."
                          className="h-7 text-xs"
                        />
                      ) : (
                        <span className="text-gray-500 text-xs">{entry.note || ""}</span>
                      )}
                    </td>
                    {editing && (
                      <td className="py-2 px-1">
                        <button onClick={() => removeRow(i)} className="text-red-400 hover:text-red-600 p-1">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editing && (
            <Button size="sm" variant="outline" className="mt-2" onClick={addRow}>
              <Plus className="h-3 w-3 mr-1" /> Add Month
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
